import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  ViewChild,
} from '@angular/core';
import {
  FeatureCountControl,
  ProjectionCode,
  ProjectionArctic,
  ProjectionAntarctic,
  ProjectionMercator,
  getProjectionCode,
} from '@models/leaflet';
import { CursorPositionControl } from '@models/leaflet';

import geojson from 'geojson';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import { MatDialog } from '@angular/material/dialog';
import { LeafletGeometryFormComponent } from './form/geometry/geometry.component';
import { LeafletGeoJSONFormComponent } from './form/geojson/geojson.component';

L.Icon.Default.imagePath = 'assets/leaflet/images/';

@Component({
  selector: 'app-leaflet',
  templateUrl: './leaflet.component.html',
  styleUrls: ['./leaflet.component.scss'],
})
export class LeafletComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapElement')
  mapElementRef!: ElementRef<HTMLElement>;

  public id = new Date().getTime().toString();
  public height = input('300px');
  public readonly = input(true);
  public projectionName = input(ProjectionCode.Mercator);
  public geometry = input<geojson.FeatureCollection>();
  public geometryChange = output<geojson.FeatureCollection>();

  private dialog = inject(MatDialog);

  private featureCountControl = new FeatureCountControl();
  private cursorPositionControl = new CursorPositionControl({
    position: 'bottomright',
  });
  private map!: L.Map;
  private drawLayer: L.GeoJSON = L.geoJSON({ type: "FeatureCollection", features: [] } as geojson.FeatureCollection);

  private projection = computed(() => {
    switch (this.projectionName()) {
      case ProjectionCode.Arctic:
        return ProjectionArctic
      case ProjectionCode.Antarctic:
        return ProjectionAntarctic
      default:
        return ProjectionMercator
    }
  });

  constructor() {
    this.drawLayer.on('pm:edit', () =>
      this.geometryChange.emit(this.getFeatureCollection()));

    effect(() => {
      const geometry = this.geometry();

      this.drawLayer.clearLayers();

      // populate draw layer
      for (const feature of geometry?.features || []) {
        if (!feature) {
          continue;
        }

        const projectionCode = getProjectionCode(feature);

        if (feature.properties?.['projection'] !== projectionCode) {
          feature.properties = { ...feature.properties, projection: projectionCode };
        }

        if (projectionCode === this.projectionName()) {
          this.drawLayer.addData(feature);
        }
      }

      // populate features count
      this.featureCountControl.set(this.drawLayer.getLayers().length);
    });
  }

  ngAfterViewInit() {
    const projection = this.projection();

    const layers = [this.drawLayer] as L.Layer[];
    for (const tileset of projection.tilesets) {
      layers.push(L.tileLayer(tileset.source, {
        tileSize: tileset.size,
        minZoom: projection.minZoom,
        maxZoom: projection.maxZoom,
        noWrap: true,
      }));
    }

    this.map = L.map(this.mapElementRef.nativeElement.id, {
      crs: projection.crs,
      maxBounds: projection.viewBounds,
      minZoom: projection.minZoom,
      maxZoom: projection.maxZoom,
      zoom: projection.defaultZoom,
      center: projection.center,
      layers: layers,
      scrollWheelZoom: true, // patched in main.ts
      doubleClickZoom: true,
      dragging: !L.Browser.mobile,
    });

    this.map.attributionControl.addAttribution('Hold Ctrl while scrolling to zoom');
    this.map.attributionControl.setPosition('bottomleft');

    this.map.addControl(this.cursorPositionControl);
    this.map.addControl(this.featureCountControl);

    if (!this.readonly()) {
      this.addDrawControls();
    }

    const drawLayers = this.drawLayer.getLayers();
    if (drawLayers.length) {
      this.map.fitBounds(this.drawLayer.getBounds(), {
        maxZoom: projection.defaultZoom,
        animate: false,
      });
    } else {
      this.map.setView(projection.center, projection.defaultZoom);
    }
  }

  ngOnDestroy() {
    this.map?.off();
    this.map?.remove();

    this.mapElementRef?.nativeElement?.remove();
  }

  private addDrawControls() {
    this.map.pm.addControls({
      position: 'topleft',
      drawRectangle: false,
      drawCircleMarker: false,
      drawCircle: false,
      rotateMode: false,
      drawText: false,
    });

    this.map.pm.Toolbar.changeActionsOfControl('removalMode', [
      'cancel',
      {
        text: 'Clear all',
        onClick: () => {
          this.drawLayer.clearLayers();
          this.geometryChange.emit(this.getFeatureCollection());
        },
      },
    ]);

    this.map.pm.Toolbar.createCustomControl({
      block: 'custom',
      name: 'custom',
      title: 'Manual input',
      className: 'leaflet-pm-icon-text',
      actions: [
        {
          text: 'Add custom geometry',
          onClick: () => this.openGeometryFormDialog(),
        },
        {
          text: 'Edit GeoJSON',
          onClick: () => this.openGeoJSONDialog(),
        },
      ],
    });

    this.map.pm.setGlobalOptions({
      layerGroup: this.drawLayer,
    });

    this.map.on('pm:create', () => {
      this.geometryChange.emit(this.getFeatureCollection());
    });
    this.map.on('pm:remove', () => {
      this.geometryChange.emit(this.getFeatureCollection());
    });
  }

  private openGeometryFormDialog() {
    const dialogRef = this.dialog.open(LeafletGeometryFormComponent, {
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((geometries?: geojson.Geometry[]) => {
      if (!geometries?.length) {
        return;
      }

      for (const geometry of geometries) {
        this.drawLayer.addData({
          type: 'Feature',
          geometry: geometry,
          properties: {
            projection: this.projectionName(),
          },
        } as geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>);
      }

      this.geometryChange.emit(this.getFeatureCollection());
    });
  }

  private openGeoJSONDialog() {
    this.dialog.open(LeafletGeoJSONFormComponent, {
      minWidth: '360px',
      width: '480px',
      disableClose: true,
      data: this.getFeatureCollection(),
    }).afterClosed().subscribe((featureCollection?: geojson.FeatureCollection) => {
      if (!featureCollection) {
        return;
      }

      this.geometryChange.emit(featureCollection);
    });
  }

  private getFeatureCollection() {
    const collection = this.drawLayer.toGeoJSON() as geojson.FeatureCollection;

    for (const feature of collection.features) {
      feature.properties = { projection: this.projectionName() };
    }

    for (const feature of this.geometry()?.features || []) {
      const projectionCode = getProjectionCode(feature)

      if (!feature.properties) {
        feature.properties = { projection: projectionCode };
      }

      if (projectionCode !== this.projectionName()) {
        collection.features.push(feature);
      }
    }

    return collection;
  };
}
